/**
 * useLayoutOrchestration Hook
 *
 * Extracts ALL orchestration logic from DesktopLayout into a shared hook that can be
 * consumed by both DesktopLayout and MobileLayout. The hook owns: auth flow, delivery
 * address flow (3-step state machine), delivery guard, location selector, order tracking,
 * contact restaurant alert, navigation (tab sync, checkout click), all store reads, all
 * derived state, service instantiation, temporary address sync, static map state, and
 * the badgedSavedAddresses memo.
 *
 * The hook must NOT contain any JSX — it returns data/handlers only.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  useRestaurantStore,
  useRewardsStore,
  useAuthStore,
  useCartStore,
  useOrderStore,
  useDeliveryStore,
  useUIStore,
  DeliveryService,
  PlacesService,
  getLocationForOrder,
  OfferWarningType,
  validateOfferStoreLocation,
  geolocationService,
  haversineDistance,
  isLocationOpenNow,
  isLocationClosingSoon,
} from '@restaurant/core'
import type { LocationInfo, LocationData, LocationBadge, DeliveryAddress, DeliveryAddressState, UserLocation, ToolbarConfig } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'

/** The loose, unsaved address shape held for logged-out users (no DB id). */
type TemporaryAddress = NonNullable<DeliveryAddressState['temporaryAddress']>
import { useDeliveryGuard } from '@restaurant/viewmodels/hooks/useDeliveryGuard'
import { useOrderStatusToast } from './useOrderStatusToast'
import { useContactRestaurantAlert } from './useContactRestaurantAlert'
import { useOfferAlerts } from './useOfferAlerts'
import { useAddressViewModel } from '../viewmodels/sheets/WebAddressViewModel'
import { useAuthViewModel } from '../viewmodels/sheets/WebAuthViewModel'
import { WebLocationService } from '@restaurant/viewmodels'
import { isShowcase } from '../showcase/showcase'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AddressFlowStep = 'address' | 'details' | 'location'
export type AddressFlowSource = 'toggle' | 'button'

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const routeTabMap: Record<string, string> = {
  '/': 'menu',
  '/menu': 'menu',
  '/offers': 'offers',
  '/rewards': 'rewards',
  '/account': 'account',
  '/account/manage': 'account',
  '/account/notifications': 'account',
  '/account/addresses': 'account',
  '/orders': 'account',
  '/favorites': 'account',
  '/help': 'account',
  '/cart': '',
  '/checkout': 'menu',
  '/reorder': '',
}

function getTabIdForRoute(pathname: string): string {
  if (routeTabMap[pathname] !== undefined) return routeTabMap[pathname]
  if (pathname.startsWith('/reorder/')) return ''
  if (pathname.startsWith('/orders/') && pathname.endsWith('/details')) return ''
  return 'menu'
}

/**
 * "City, ST 12345" from whatever parts exist; '' when none do.
 * The old template-string version rendered a literal "," when city/state
 * were blank (latent for real tenants, guaranteed for showcase's
 * render-inert location stubs). Zip is suppressed when city AND state are
 * blank — a bare zip line looks broken.
 */
export function composeAddressLine2(city?: string, state?: string, zipCode?: string): string {
  const c = (city ?? '').trim()
  const s = (state ?? '').trim()
  if (!c && !s) return ''
  const stateZip = [s, (zipCode ?? '').trim()].filter(Boolean).join(' ')
  return [c, stateZip].filter(Boolean).join(', ')
}

export function useLayoutOrchestration() {
  const navigate = useNavigate()
  const location = useLocation()

  // -----------------------------------------------------------------------
  // Store reads
  // -----------------------------------------------------------------------

  const { currentRestaurant, currentLocation: storeLocation, locations, setLocation, isDebugMode } = useRestaurantStore()
  const { availableOffers, availableRewards, merchantConfig } = useRewardsStore()
  const { isAuthenticated, user } = useAuthStore()
  const { items, getFormattedTotal, appliedOfferId } = useCartStore()
  const { orderType, setOrderType, orderHistory } = useOrderStore()
  const { currentAddress, temporaryAddress, savedAddresses, setCurrentAddress, setTemporaryAddress, clearTemporaryAddress } = useDeliveryStore()

  // -----------------------------------------------------------------------
  // ViewModels
  // -----------------------------------------------------------------------

  // Auth ViewModel — primary auth state and action source post-migration
  const vm = useAuthViewModel()

  // Address ViewModel integration
  const addressViewModel = useAddressViewModel()

  // -----------------------------------------------------------------------
  // Services (memoised)
  // -----------------------------------------------------------------------

  const deliveryService = useMemo(() => new DeliveryService({ apiClient }), [])
  const placesService = useMemo(() => new PlacesService({ apiClient }), [])

  // -----------------------------------------------------------------------
  // Delivery guard
  // -----------------------------------------------------------------------

  const deliveryGuard = useDeliveryGuard({
    currentAddress,
    savedAddresses,
    currentLocation: storeLocation,
    locations,
    businessId: currentRestaurant?.id ?? null,
    orderType,
    setOrderType,
    setCurrentAddress,
    updateAddressFees: useDeliveryStore.getState().updateAddressFees,
    setDeliveryFee: useCartStore.getState().setDeliveryFee,
    deliveryService,
    user,
  })

  // -----------------------------------------------------------------------
  // Derived state
  // -----------------------------------------------------------------------

  const hasMultipleLocations = (currentRestaurant?.locations?.length ?? 0) > 1

  // Showcase renders must show the full product silhouette: with the data
  // hooks gated, availableOffers/rewards are empty and these tabs would
  // vanish (spec §6) — a two-tab bar undersells the product.
  const showOffers = isShowcase() || (availableOffers && availableOffers.length > 0)
  const hasDollarRedemption = merchantConfig?.redemption_type === 'dollar' &&
    merchantConfig?.point_value && merchantConfig?.dollar_value
  const hasItemRedemption = merchantConfig?.item_redemption_groups &&
    merchantConfig.item_redemption_groups.length > 0
  const showRewards = isShowcase() || Boolean(hasDollarRedemption || hasItemRedemption) ||
    (availableRewards && availableRewards.length > 0)

  const checkoutTotal = getFormattedTotal()
  const checkoutButtonState: 'Hidden' | 'Visible' = items.length > 0 ? 'Visible' : 'Hidden'

  const deliveryAddress = orderType === 'Delivery' ? (currentAddress || temporaryAddress) : null

  const currentLocation: LocationInfo = storeLocation ? {
    name: storeLocation.name || 'Location',
    addressLine1: deliveryAddress
      ? deliveryAddress.street
      : (storeLocation.address || ''),
    addressLine2: deliveryAddress
      ? composeAddressLine2(deliveryAddress.city, deliveryAddress.state, deliveryAddress.zipCode)
      : composeAddressLine2(storeLocation.city, storeLocation.state, storeLocation.zipCode),
  } : {
    name: 'Select Location',
    addressLine1: '',
    addressLine2: 'Choose a location to get started'
  }

  // -----------------------------------------------------------------------
  // Navigation state
  // -----------------------------------------------------------------------

  const [selectedTabId, setSelectedTabId] = useState<string>(() => getTabIdForRoute(location.pathname))
  const [pendingAccountNavigation, setPendingAccountNavigation] = useState(false)

  // -----------------------------------------------------------------------
  // Location selector sheet state
  // -----------------------------------------------------------------------

  const [isLocationSheetOpen, setIsLocationSheetOpen] = useState(false)

  // -----------------------------------------------------------------------
  // User geolocation (for sorting location list by distance)
  // -----------------------------------------------------------------------

  const [userGeo, setUserGeo] = useState<UserLocation | null>(null)

  useEffect(() => {
    if (isShowcase()) return
    geolocationService.getLocation().then((loc) => {
      if (loc) setUserGeo(loc)
    })
  }, [])

  // -----------------------------------------------------------------------
  // Delivery address sheet state
  // -----------------------------------------------------------------------

  const [isDeliveryAddressSheetOpen, setIsDeliveryAddressSheetOpen] = useState(false)
  const [addressWasSelected, setAddressWasSelected] = useState(false)
  const [addressFlowStep, setAddressFlowStep] = useState<AddressFlowStep>('address')
  const [addressFlowSource, setAddressFlowSource] = useState<AddressFlowSource>('toggle')
  const previousAddressRef = useRef<DeliveryAddress | TemporaryAddress | null>(null)

  // -----------------------------------------------------------------------
  // Static map state for address details sheet
  // -----------------------------------------------------------------------

  const [staticMapUrl, setStaticMapUrl] = useState<string | null>(null)
  const [staticMapLoading, setStaticMapLoading] = useState(false)
  const [staticMapError, setStaticMapError] = useState(false)

  const selectedLat = addressViewModel.selectedAddress?.latitude
  const selectedLng = addressViewModel.selectedAddress?.longitude

  // -----------------------------------------------------------------------
  // UI store — sheet state and favorites count
  // -----------------------------------------------------------------------

  const {
    isFavoriteProductSheetOpen,
    selectedFavoriteProduct,
    closeFavoriteProductSheet,
    isReorderProductSheetOpen,
    selectedReorderProduct,
    closeReorderProductSheet,
    favoritesCount,
    authRequested,
    clearAuthRequest,
    isAuthSheetOpen,
    setIsAuthSheetOpen,
    locationSheetRequested,
    clearLocationSheetRequest,
    recoveryRequested,
    clearRecoveryRequest,
  } = useUIStore()

  const openAuthSheet = useCallback(() => {
    setIsAuthSheetOpen(true)
    vm.setCurrentFlow('signin-phone')
    vm.clearErrors()
  }, [setIsAuthSheetOpen, vm])

  const closeAuthSheet = useCallback(() => {
    setIsAuthSheetOpen(false)
  }, [setIsAuthSheetOpen])

  // -----------------------------------------------------------------------
  // Effects
  // -----------------------------------------------------------------------

  // Close auth sheet when navigation occurs (e.g. navigateToHome() after successful sign-in).
  // closeAuthSheet is a stable useCallback reference (Zustand setter), so adding it to deps
  // does not change when this effect re-runs — it still only fires on pathname changes.
  useEffect(() => {
    closeAuthSheet()
  }, [location.pathname, closeAuthSheet])

  // Auth integration: sync temporary address when user authenticates
  useEffect(() => {
    if (isShowcase()) return
    const syncTemporaryAddressOnAuth = async () => {
      // Only sync if user just authenticated and has a temporary address
      if (isAuthenticated && temporaryAddress && user) {
        try {
          // Check if this address already exists in the user's saved addresses
          const alreadyExists = savedAddresses.some(
            (a) => a.street === temporaryAddress.street &&
                   a.city === temporaryAddress.city &&
                   a.zipCode === temporaryAddress.zipCode
          )

          let backendAddress: DeliveryAddress | null = null

          if (!alreadyExists) {
            // Push address to backend — get real backend ID back
            const saved = await deliveryService.saveUserAddress({
              userId: user.id,
              street: temporaryAddress.street,
              city: temporaryAddress.city,
              state: temporaryAddress.state,
              zipCode: temporaryAddress.zipCode,
              businessId: currentRestaurant?.id ?? '',
              apartment: temporaryAddress.apartment,
              entryCode: temporaryAddress.entryCode,
              buildingName: temporaryAddress.buildingName,
              deliveryDetails: temporaryAddress.deliveryDetails,
              latitude: temporaryAddress.latitude,
              longitude: temporaryAddress.longitude,
              storeDeliveryFees: temporaryAddress.storeDeliveryFees ?? {},
            })
            backendAddress = saved
          } else {
            // Address already exists on backend — use the existing one
            backendAddress = savedAddresses.find(
              (a) => a.street === temporaryAddress.street &&
                     a.city === temporaryAddress.city &&
                     a.zipCode === temporaryAddress.zipCode
            ) ?? null
          }

          if (backendAddress) {
            // Merge any locally-cached fees into the backend address
            const mergedFees = {
              ...backendAddress.storeDeliveryFees,
              ...temporaryAddress.storeDeliveryFees,
            }

            setCurrentAddress({
              ...backendAddress,
              storeDeliveryFees: mergedFees,
            })

            // Persist fees to backend (fire-and-forget)
            if (Object.keys(temporaryAddress.storeDeliveryFees ?? {}).length > 0) {
              deliveryService.updateStoreDeliveryFees(backendAddress.id, mergedFees).catch(() => {})
            }

            // Background: fetch remaining location fees with the real backend ID
            deliveryGuard.fetchRemainingFees({
              ...backendAddress,
              storeDeliveryFees: mergedFees,
            }).catch(() => {})
          }

          // Clear the temporary address after promoting
          clearTemporaryAddress()
        } catch (error) {
          // Temporary address sync failed — non-critical, user can re-enter
          // Still clear temp to avoid retrying on every render
          clearTemporaryAddress()
        }
      }
    }

    syncTemporaryAddressOnAuth()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deliveryService and fetchRemainingFees are stable (memoized), safe to omit
  }, [isAuthenticated, user, temporaryAddress, savedAddresses.length, setCurrentAddress, clearTemporaryAddress])

  // Hydration check — validate persisted Delivery selection on mount only.
  // Separated from the address-sync effect so that savedAddresses.length
  // changes (from saving a new address mid-flow) don't re-trigger it.
  useEffect(() => {
    deliveryGuard.checkHydratedAddress()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally mount-only
  }, [])

  // Update selected tab based on current route
  useEffect(() => {
    setSelectedTabId(getTabIdForRoute(location.pathname))
  }, [location.pathname])

  // Navigate to /account after successful auth when user was trying to access account.
  // Skip if signup flow is active — user still needs to complete their profile first.
  useEffect(() => {
    if (isAuthenticated && pendingAccountNavigation && vm.currentFlow !== 'signup') {
      setPendingAccountNavigation(false)
      navigate('/account')
    }
  }, [isAuthenticated, pendingAccountNavigation, vm.currentFlow, navigate])

  // Clear pending navigation when auth sheet closes without successful auth
  useEffect(() => {
    if (!isAuthSheetOpen && pendingAccountNavigation && !isAuthenticated) {
      setPendingAccountNavigation(false)
    }
  }, [isAuthSheetOpen, pendingAccountNavigation, isAuthenticated])

  // Static map fetch
  useEffect(() => {
    if (isShowcase()) return
    if (selectedLat == null || selectedLng == null) {
      setStaticMapUrl(null)
      setStaticMapError(false)
      return
    }

    let cancelled = false
    setStaticMapLoading(true)
    setStaticMapError(false)
    setStaticMapUrl(null)

    placesService
      .buildStaticMapUrl({ latitude: selectedLat, longitude: selectedLng, zoom: 18, size: '750x400' })
      .then((url) => {
        if (!cancelled) {
          setStaticMapUrl(url)
          setStaticMapLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStaticMapError(true)
          setStaticMapLoading(false)
        }
      })

    return () => { cancelled = true }
  }, [selectedLat, selectedLng, placesService])

  // -----------------------------------------------------------------------
  // Memos
  // -----------------------------------------------------------------------

  const locationService = useMemo(() => new WebLocationService(null, isDebugMode), [isDebugMode])

  const locationDataList = useMemo((): LocationData[] => {
    // Look up offer location restrictions
    let offerStoreLocationIds: Set<string> | null = null
    if (appliedOfferId) {
      const offer = availableOffers.find((o) => o.id === appliedOfferId)
      if (offer && offer.storeLocations.length > 0) {
        offerStoreLocationIds = new Set(offer.storeLocations.map((sl) => sl.storeId))
      }
    }

    // Determine reference point for distance sorting:
    // Pickup → user's IP geolocation, Delivery (or delivery flow in progress) → current delivery address
    const effectiveAddress = currentAddress || temporaryAddress
    const isDeliveryContext = orderType === 'Delivery' || addressFlowStep === 'location'
    const sortOrigin: { lat: number; lng: number } | null =
      isDeliveryContext && effectiveAddress?.latitude != null && effectiveAddress?.longitude != null
        ? { lat: effectiveAddress.latitude, lng: effectiveAddress.longitude }
        : userGeo

    // Pre-compute distances for sorting
    const distanceMap = new Map<string, number>()
    if (sortOrigin) {
      for (const loc of locations) {
        distanceMap.set(loc.id, haversineDistance(sortOrigin.lat, sortOrigin.lng, loc.latitude, loc.longitude))
      }
    }

    const list = locations.map((loc) => {
      const addressParts = [loc.address, loc.city, loc.state, loc.zipCode].filter(Boolean)
      const locationAddress = addressParts.join(', ')
      const hoursText = locationService.formatHoursText(loc)

      // Format distance text
      let distanceText = ''
      const km = distanceMap.get(loc.id)
      if (km != null) {
        const mi = km * 0.621371
        distanceText = mi < 0.1 ? 'Nearby' : `(${mi.toFixed(1)} miles away)`
      }

      const isAvailable = locationService.checkLocationAvailability(loc)
      const isSelected = loc.id === storeLocation?.id

      const data: LocationData = {
        id: loc.id,
        locationName: loc.name || 'Unknown Location',
        locationAddress,
        hoursText,
        distanceText,
        isSelected,
        isClosingSoon: isLocationClosingSoon(loc),
      }

      if (!loc.isActive || loc.isOrdersPaused || !isLocationOpenNow(loc)) {
        data.disabled = true
      }

      let isOutOfRange = false
      if (isDeliveryContext && currentAddress) {
        data.deliveryStatus = deliveryGuard.getLocationDeliveryStatus(loc.id)
        data.deliveryFee = deliveryGuard.getLocationDeliveryFee(loc.id) ?? undefined
        data.disabled = data.disabled || data.deliveryStatus === 'out-of-range' || data.deliveryStatus === 'unchecked'
        data.isFeeLoading = data.deliveryStatus === 'unchecked'
        isOutOfRange = data.deliveryStatus === 'out-of-range'
      }

      const isOfferNotAccepted = offerStoreLocationIds != null && !offerStoreLocationIds.has(loc.id)

      const badges: LocationBadge[] = []
      if (isSelected) badges.push({ text: 'Selected', variant: 'Default' })
      if (isOutOfRange) badges.push({ text: 'Out of Delivery Range', variant: 'Warning' })
      if (!isAvailable) badges.push({ text: 'Not Accepting Orders', variant: 'Warning' })
      if (isOfferNotAccepted) badges.push({ text: 'Offer Not Accepted', variant: 'Warning' })

      if (badges.length > 0) {
        data.badges = badges
      }

      return data
    })

    // Sort by distance (closest first)
    if (sortOrigin) {
      list.sort((a, b) => (distanceMap.get(a.id) ?? Infinity) - (distanceMap.get(b.id) ?? Infinity))
    }

    return list
  }, [locations, storeLocation?.id, locationService, orderType, addressFlowStep, currentAddress, temporaryAddress, deliveryGuard, appliedOfferId, availableOffers, userGeo])

  const badgedSavedAddresses = useMemo(() => {
    if (!isAuthenticated && temporaryAddress) {
      return [{
        id: 'temp-address',
        addressLine1: temporaryAddress.street,
        addressLine2: composeAddressLine2(temporaryAddress.city, temporaryAddress.state, temporaryAddress.zipCode),
      }]
    }

    if (savedAddresses.length === 0) return []

    return savedAddresses
      .slice()
      .sort((a, b) => new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime())
      .map((addr) => ({
        id: addr.id,
        addressLine1: addr.street,
        addressLine2: composeAddressLine2(addr.city, addr.state, addr.zipCode),
      }))
  }, [isAuthenticated, temporaryAddress, savedAddresses])

  // -----------------------------------------------------------------------
  // Order tracking
  // -----------------------------------------------------------------------

  const orderStatusToast = useOrderStatusToast()
  const { activeOrder, isTrackingSheetOpen, activeOrderTrackingStatus, setTrackingSheetOpen } = useOrderStore()
  const activeOrderDriverLocation = useOrderStore((s) => s.activeOrderDriverLocation)

  // Contact restaurant alert
  const contactAlert = useContactRestaurantAlert()

  // Offer warning alerts
  const offerAlert = useOfferAlerts({
    onAutoRemoval: (warningType) => {
      // These alert types are handled by CartPage — suppress here to prevent duplicates
      if (warningType === OfferWarningType.minimumOrderNotMet ||
          warningType === OfferWarningType.missingRequiredItem) {
        return null
      }
      return {}
    },
  })

  // Open auth sheet when a page requests it (e.g. unauthenticated favorite)
  useEffect(() => {
    if (authRequested && !isAuthenticated) {
      openAuthSheet()
      clearAuthRequest()
    } else if (authRequested) {
      clearAuthRequest()
    }
  }, [authRequested, isAuthenticated, openAuthSheet, clearAuthRequest])

  // Open auth sheet in 'set-new-password' mode when Supabase PASSWORD_RECOVERY
  // fires (user clicked a password-reset email link).
  useEffect(() => {
    if (!recoveryRequested) return
    setIsAuthSheetOpen(true)
    vm.setCurrentFlow('set-new-password')
    vm.clearErrors()
    clearRecoveryRequest()
  }, [recoveryRequested, setIsAuthSheetOpen, vm, clearRecoveryRequest])

  // Open location sheet when a page requests it (e.g. closed-location alert)
  useEffect(() => {
    if (locationSheetRequested) {
      setIsLocationSheetOpen(true)
      clearLocationSheetRequest()
    }
  }, [locationSheetRequested, clearLocationSheetRequest])

  // QuickButtonsContainer visibility
  const showQuickButtons = useMemo(() => {
    if (location.pathname !== '/menu' && location.pathname !== '/') return false
    return true
  }, [location.pathname])

  const hasFavorites = isAuthenticated && favoritesCount > 0
  const hasOrderHistory = isAuthenticated && (orderHistory?.length ?? 0) > 0

  const categories = useRestaurantStore((state) => state.categories)
  const [toolbarSearchActive, setToolbarSearchActive] = useState(false)
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined)
  const selectedCategoryIdRef = useRef(selectedCategoryId)
  selectedCategoryIdRef.current = selectedCategoryId
  const isScrollingToCategoryRef = useRef(false)
  const scrollingToCategoryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const firstCategory = categories[0]
    if (!selectedCategoryId && firstCategory) {
      setSelectedCategoryId(firstCategory.id)
    }
  }, [categories, selectedCategoryId])

  const isMenuPage = location.pathname === '/menu' || location.pathname === '/'
  const toolbar: ToolbarConfig | undefined = useMemo(() => {
    if (!isMenuPage || categories.length === 0) return undefined
    return {
      categories: categories.map((cat) => ({ id: cat.id, label: cat.name })),
      selectedCategoryId,
      searchActive: toolbarSearchActive,
      searchButtonPosition: 'hidden',
      onCategoryPress: (id: string) => {
        setSelectedCategoryId(id)
        isScrollingToCategoryRef.current = true
        if (scrollingToCategoryTimerRef.current) clearTimeout(scrollingToCategoryTimerRef.current)
        scrollingToCategoryTimerRef.current = setTimeout(() => { isScrollingToCategoryRef.current = false }, 800)
        // Scroll only the app's own <main> container. scrollIntoView scrolls
        // every ancestor scrolling box, which in an iframe (webeatery.app/preview
        // device frames) makes Firefox scroll the host page too.
        const el = document.getElementById(`category-${id}`)
        const container = el?.closest('main')
        if (!el || !container) return
        const scrollMargin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0
        container.scrollTo({
          top:
            el.getBoundingClientRect().top -
            container.getBoundingClientRect().top +
            container.scrollTop -
            scrollMargin,
          behavior: 'smooth',
        })
      },
      onSearchPress: () => setToolbarSearchActive(true),
      onCancelSearch: () => setToolbarSearchActive(false),
    }
  }, [isMenuPage, categories, selectedCategoryId, toolbarSearchActive])

  const handleScrollSpy = useCallback((scrollContainer: HTMLElement) => {
    if (!isMenuPage || isScrollingToCategoryRef.current || toolbarSearchActive) return
    if (categories.length === 0) return

    const containerRect = scrollContainer.getBoundingClientRect()
    const pinnedHeaderPx = parseFloat(
      getComputedStyle(scrollContainer).getPropertyValue('--pinned-header-height')
    ) || 0
    const spyLine = containerRect.top + pinnedHeaderPx + 110

    let activeCategoryId: string | undefined
    for (const cat of categories) {
      const el = document.getElementById(`category-${cat.id}`)
      if (!el) continue
      if (el.getBoundingClientRect().top <= spyLine) {
        activeCategoryId = cat.id
      }
    }

    if (!activeCategoryId && categories[0]) {
      activeCategoryId = categories[0].id
    }

    if (activeCategoryId && activeCategoryId !== selectedCategoryIdRef.current) {
      setSelectedCategoryId(activeCategoryId)
    }
  }, [isMenuPage, toolbarSearchActive, categories])

  useEffect(() => {
    return () => {
      if (scrollingToCategoryTimerRef.current) clearTimeout(scrollingToCategoryTimerRef.current)
    }
  }, [])

  // -----------------------------------------------------------------------

  const handleContactRestaurant = useCallback(() => {
    if (!activeOrder) return
    contactAlert.showForOrder(activeOrder)
  }, [activeOrder, contactAlert])

  const activeOrderMapLocation = useMemo(() => {
    if (!activeOrder || !currentRestaurant) return null
    const loc = getLocationForOrder(activeOrder, currentRestaurant, storeLocation ?? undefined)
    return {
      lat: loc.latitude || 0,
      lng: loc.longitude || 0,
      name: currentRestaurant.name,
    }
  }, [activeOrder, currentRestaurant, storeLocation])

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleTabChange = useCallback((tabId: string) => {
    if (tabId === 'account' && !isAuthenticated) {
      setPendingAccountNavigation(true)
      openAuthSheet()
      return
    }

    setSelectedTabId(tabId)

    const tabRouteMap: Record<string, string> = {
      'menu': '/menu',
      'offers': '/offers',
      'rewards': '/rewards',
      'account': '/account'
    }

    const route = tabRouteMap[tabId]
    const alreadyOnRoute = route && (location.pathname === route || (tabId === 'menu' && location.pathname === '/'))
    if (alreadyOnRoute) {
      document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' })
    } else if (route && location.pathname !== route) {
      navigate(route)
    }
  }, [isAuthenticated, openAuthSheet, location.pathname, navigate])

  const handleCheckoutClick = useCallback(() => {
    if (!isAuthenticated) {
      openAuthSheet()
      return
    }
    navigate('/cart')
  }, [isAuthenticated, openAuthSheet, navigate])

  const handleChangeLocationPress = useCallback(() => {
    if (orderType === 'Delivery') {
      setAddressFlowSource('button')
      previousAddressRef.current = currentAddress || temporaryAddress || null
      setIsDeliveryAddressSheetOpen(true)
    } else {
      setIsLocationSheetOpen(true)
    }
  }, [orderType, currentAddress, temporaryAddress])

  const handleLocationSheetClose = useCallback(() => {
    setIsLocationSheetOpen(false)

    // If an offer is applied with location restrictions and the current location
    // doesn't support it, silently remove the offer
    const cartState = useCartStore.getState()
    if (cartState.appliedOfferId && storeLocation) {
      const offer = useRewardsStore.getState().availableOffers.find(
        (o) => o.id === cartState.appliedOfferId
      )
      if (offer && validateOfferStoreLocation(offer, storeLocation.id).status !== 'valid') {
        cartState.removeAppliedOffer()
      }
    }
  }, [storeLocation])

  const handleLocationSelect = useCallback((loc: LocationData) => {
    const fullLocation = locations.find(l => l.id === loc.id)
    if (!fullLocation) return

    /**
     * Helper that applies the location change (delivery fee logic + close sheet).
     * Extracted so the offer-validation dialog callbacks can invoke it after
     * the user makes a decision.
     */
    const applyLocationChange = () => {
      if (orderType === 'Delivery') {
        const status = deliveryGuard.getLocationDeliveryStatus(fullLocation.id)
        if (status === 'available') {
          setLocation(fullLocation)
          const fee = deliveryGuard.getLocationDeliveryFee(fullLocation.id)
          if (fee !== null) useCartStore.getState().setDeliveryFee(fee)
        } else {
          setLocation(fullLocation)
          setOrderType('Pickup')
          useCartStore.getState().setDeliveryFee(0)
        }
      } else {
        setLocation(fullLocation)
      }
      setIsLocationSheetOpen(false)
    }

    // --- Offer location check: silently remove if invalid ---
    const cartState = useCartStore.getState()
    if (cartState.appliedOfferId) {
      const offer = useRewardsStore.getState().availableOffers.find(
        (o) => o.id === cartState.appliedOfferId
      )
      if (offer && validateOfferStoreLocation(offer, fullLocation.id).status !== 'valid') {
        cartState.removeAppliedOffer()
      }
    }

    applyLocationChange()
  }, [locations, orderType, deliveryGuard, setLocation, setOrderType])

  const handleOrderTypeChange = useCallback((newOrderType: 'Pickup' | 'Delivery') => {
    if (newOrderType === 'Pickup') {
      setOrderType('Pickup')
      useCartStore.getState().setDeliveryFee(0)
    } else {
      // Show Delivery selected immediately for visual feedback.
      // Rollback handlers revert to Pickup if the flow is cancelled.
      setOrderType('Delivery')
      setAddressFlowSource('toggle')
      previousAddressRef.current = currentAddress || temporaryAddress || null
      setIsDeliveryAddressSheetOpen(true)
    }
  }, [setOrderType, currentAddress, temporaryAddress])

  const resetAddressFlowState = useCallback(() => {
    setIsDeliveryAddressSheetOpen(false)
    setAddressFlowStep('address')
    previousAddressRef.current = null
    addressViewModel.goBackToSearch()
  }, [addressViewModel])

  const handleDeliveryAddressSheetClose = useCallback(() => {
    if (addressFlowSource === 'toggle' && !addressWasSelected) {
      setOrderType('Pickup')
      useCartStore.getState().setDeliveryFee(0)

      if (!isAuthenticated) {
        setCurrentAddress(null)
      }
    }

    resetAddressFlowState()
    setAddressWasSelected(false)
  }, [addressFlowSource, addressWasSelected, isAuthenticated, setOrderType, setCurrentAddress, resetAddressFlowState])

  const handleDeliveryLocationStepClose = useCallback(() => {
    const prev = previousAddressRef.current

    if (addressFlowSource === 'toggle') {
      setOrderType('Pickup')
      useCartStore.getState().setDeliveryFee(0)
    }

    // Rollback to previous address
    if (prev && 'id' in prev) {
      // A real, saved DeliveryAddress
      setCurrentAddress(prev)

      // Fire-and-forget lastUsed PATCH for authenticated users
      if (isAuthenticated && prev.id && prev.id !== 'temp-address') {
        deliveryService.updateUserAddress(prev.id, { lastUsed: new Date().toISOString() }).catch(() => {})
      }
    } else if (prev) {
      // An unsaved temporary address (logged-out user) — restore as temporary
      setTemporaryAddress(prev)
    }

    previousAddressRef.current = null
    resetAddressFlowState()
    setAddressWasSelected(false)
  }, [addressFlowSource, isAuthenticated, setOrderType, setCurrentAddress, setTemporaryAddress, deliveryService, resetAddressFlowState])

  // -----------------------------------------------------------------------
  // Return value — consumed by DesktopLayout and MobileLayout
  // -----------------------------------------------------------------------

  return {
    // Navigation
    navigate,
    location,

    // Store data
    currentRestaurant,
    storeLocation,
    locations,
    setLocation,
    isDebugMode,
    isAuthenticated,
    user,
    items,
    orderType,
    setOrderType,
    currentAddress,
    temporaryAddress,
    savedAddresses,
    setCurrentAddress,
    setTemporaryAddress,
    clearTemporaryAddress,

    // ViewModels
    vm,
    isAuthSheetOpen,
    closeAuthSheet,
    addressViewModel,

    // Services
    deliveryService,
    deliveryGuard,

    // Derived state
    hasMultipleLocations,
    showOffers,
    showRewards,
    checkoutTotal,
    checkoutButtonState,
    currentLocation,
    selectedTabId,

    // Location sheet
    isLocationSheetOpen,
    locationDataList,

    // Delivery address sheet
    isDeliveryAddressSheetOpen,
    setIsDeliveryAddressSheetOpen,
    addressWasSelected,
    setAddressWasSelected,
    addressFlowStep,
    setAddressFlowStep,
    badgedSavedAddresses,
    staticMapUrl,
    staticMapLoading,
    staticMapError,
    selectedLat,
    selectedLng,
    resetAddressFlowState,

    // Order tracking
    orderStatusToast,
    activeOrder,
    isTrackingSheetOpen,
    activeOrderTrackingStatus,
    setTrackingSheetOpen,
    activeOrderDriverLocation,
    activeOrderMapLocation,

    // Contact restaurant alert
    contactAlert,
    handleContactRestaurant,

    // Offer warning alerts
    offerAlert,

    // QuickButtonsContainer
    showQuickButtons,
    hasFavorites,
    hasOrderHistory,

    // Toolbar
    toolbar,
    handleScrollSpy,

    // Favorite product sheet
    isFavoriteProductSheetOpen,
    selectedFavoriteProduct,
    closeFavoriteProductSheet,

    // Reorder product sheet
    isReorderProductSheetOpen,
    selectedReorderProduct,
    closeReorderProductSheet,

    // Handlers
    handleTabChange,
    handleCheckoutClick,
    handleChangeLocationPress,
    handleLocationSheetClose,
    handleLocationSelect,
    handleOrderTypeChange,
    handleDeliveryAddressSheetClose,
    handleDeliveryLocationStepClose,
  }
}

/** Return type of the orchestration hook — used for typing in layout components. */
export type LayoutOrchestration = ReturnType<typeof useLayoutOrchestration>
